import { HttpClient, HttpEvent, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ClassType } from 'class-transformer/ClassTransformer';
import { cloneDeep } from 'lodash';
import { Observable, throwError } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { HttpMethod } from '../../common/model/http-method';
import { SortBy } from '../model/data-source';
import { Operator } from '../model/operator';
import { excludeColumnFilterCriteria, RequestCriteria } from '../model/request-criteria';
import { RestRequest } from '../model/rest-request';
import { ResponseWrapper, RestResponse } from '../model/rest-response';
import { RestSimpleRequest } from '../model/rest-simple-request';
import { transformDataToClass } from '../util/rest-service-util';

@Injectable({
  providedIn: 'root'
})
export abstract class RestService {

  protected serverUrl: string;

  constructor(protected http: HttpClient) {
    this.serverUrl = environment.serverUrl;
  }

  protected getAndExtract<T>(type: ClassType<T>, endpoint: string, headers: HttpHeaders | null = null): Observable<T[]> {
    return this.request(HttpMethod.GET, type, endpoint, null, null, headers)
      .pipe(map(response => response.data));
  }

  protected getAndExtractSingle<T>(type: ClassType<T>, endpoint: string): Observable<T> {
    return this.requestAndExtractSingle<T>(HttpMethod.GET, type, endpoint);
  }

  protected postAndExtract<T>(type: ClassType<T>, endpoint: string, body?: any | null): Observable<T[]> {
    return this.request(HttpMethod.POST, type, endpoint, body).pipe(map(response => response.data));
  }

  protected postAndExtractSingle<T>(type: ClassType<T>, endpoint: string, body?: any | null): Observable<T> {
    return this.requestAndExtractSingle<T>(HttpMethod.POST, type, endpoint, body);
  }

  protected putAndExtractSingle<T>(type: ClassType<T>, endpoint: string, body?: any | null): Observable<T> {
    return this.requestAndExtractSingle<T>(HttpMethod.PUT, type, endpoint, body);
  }

  protected putAndExtract<T>(type: ClassType<T>, endpoint: string, body?: any | null): Observable<T[]> {
    return this.request(HttpMethod.PUT, type, endpoint, body).pipe(map(response => response.data));
  }

  protected deleteAndExtractSingle<T>(type: ClassType<T>, endpoint: string, body?: any | null): Observable<T> {
    return this.requestAndExtractSingle<T>(HttpMethod.DELETE, type, endpoint, body);
  }

  protected requestAndExtractSingle<T>(httpMethod: HttpMethod, type: ClassType<T>, endpoint: string, body?: any | null): Observable<T> {
    return this.request(httpMethod, type, endpoint, body)
      .pipe(
        tap(response => { if (response == null || response.data[0] == null) throwError(response); }),
        map(response => response.data[0])
      );
  }

  protected postRequest<T>(type: ClassType<T>, endpoint: string, body?: any | null, headers?: HttpHeaders | null): Observable<RestResponse<T>> {
    return this.request(HttpMethod.POST, type, endpoint, body, null, headers);
  }

  protected getRequestWithSimpleResponse<T>(endpoint: string, body?: any | null, isParam?: boolean | null): Observable<T> {
    return this.requestWithSimpleResponse<T>(HttpMethod.GET, endpoint, body, isParam);
  }

  protected requestWithSimpleResponse<T>(httpMethod: HttpMethod, endpoint: string, body?: any | null, isParam?: boolean | null): Observable<T> {
    const data = isParam ? { params: body } : { body };
    return this.http.request<T>(httpMethod, `${this.serverUrl}${endpoint}`, data);
  }

  protected request<T>(
    httpMethod: HttpMethod, type: ClassType<T>, endpoint: string, bodyData?: any | null, paramsData?: HttpParams | null, headers?: HttpHeaders | null
  ): Observable<RestResponse<T>> {
    const options = { params: paramsData, body: bodyData, headers };
    return this.http.request<ResponseWrapper<any>>(httpMethod, `${this.serverUrl}${endpoint}`, options)
      .pipe(
        tap(wrapper => {
          if (wrapper.response.status < 0) {
            throw wrapper.response;
          }
        }),
        map(wrapper => {
          const response = wrapper.response;
          transformDataToClass(response.data, type);
          return response;
        })
      );
  }

  protected doGet<T>(url: string, params: any = null, headers: Record<string, string> = null): Observable<T> {
    return this.http.get<T>(`${this.serverUrl}${url}`, { params, headers });
  }

  protected doPost<T>(url: string, body: any = {}, headers: any = {}): Observable<T> {
    return this.http.post<T>(`${this.serverUrl}${url}`, body, { headers });
  }

  protected doPatch<T>(url: string, body: any = {}): Observable<T> {
    return this.http.patch<T>(`${this.serverUrl}${url}`, body);
  }

  protected doPostWithOptions<T>(url: string, body: any = {}, options: any = {}): Observable<HttpEvent<T>> {
    return this.http.post<T>(`${this.serverUrl}${url}`, body, options);
  }

  protected doPut<T>(url: string, body: any): Observable<T> {
    return this.http.put<T>(`${this.serverUrl}${url}`, body);
  }

  protected doDelete<T>(url: string, body: any = {}): Observable<HttpEvent<T>> {
    return this.http.delete<T>(`${this.serverUrl}${url}`, body);
  }

  protected doGetWithOptions<T>(url: string, options: any = {}): Observable<HttpEvent<T>> {
    return this.http.get<T>(`${this.serverUrl}${url}`, options);
  }

  protected doGetBlobWithParams(url: string, params: any = null, headers: Record<string, string> = null): Observable<HttpResponse<Blob>> {
    return this.http.get(`${this.serverUrl}${url}`, { responseType: 'arraybuffer', headers, params }).pipe(
      map(rs => rs as unknown as HttpResponse<Blob>)
    );
  }

  protected doGetBlob(url: string): Observable<HttpResponse<Blob>> {
    return this.doGetWithOptions(url, { responseType: 'blob', observe: 'response' })
      .pipe(
        map(rs => rs as unknown as HttpResponse<Blob>)
      );
  }

  protected prepareRestRequest(startRow: number, endRow: number, dataPageSize: number, filters: RequestCriteria,
    sorters?: SortBy[]): RestRequest<RequestCriteria> {
    const request: RestRequest<RequestCriteria> = {
      startRow,
      endRow,
      dataPageSize,
      data: excludeColumnFilterCriteria(filters)
    };

    if (sorters)
      request.sortBy = sorters.map(c => c.toSmartGwtSort());
    return request;
  }

  protected addToCriteria(req: RestSimpleRequest<RequestCriteria>, fieldName: string, value: any, operator: Operator): RestSimpleRequest<RequestCriteria> {
    const newReq = cloneDeep(req);
    const toAdd: RequestCriteria = { fieldName, operator, value };
    if (req.data?.criteria == null)
      return Object.assign(newReq, { data: { ...newReq.data, criteria: [toAdd] } });
    else
      return Object.assign(newReq, { data: { ...newReq.data, criteria: [...newReq.data.criteria, toAdd] } });
  }

  downloadFile(data: HttpResponse<Blob>, fileName: string): void {
    const a = document.createElement('a');
    a.href = window.URL.createObjectURL(data.body);
    a.download = fileName;
    a.click();
  }

}
